/**
 * The answer passage and Q&A for one endpoint page (/api/:apiId/:endpointId).
 *
 * These pages extract to 15 to 23 words of prose for a crawler that does not
 * run JavaScript, and none of those words is the price: the USD rate reaches
 * the page only inside the routing table's markup and the Service JSON-LD, so
 * a passage extractor reconstructs neither. That is the gap this closes.
 *
 * It closes it on the axis this page owns - what the call returns, how it
 * routes, what it costs, how it has behaved - and deliberately not on the axis
 * the Mintlify reference at /docs/api-reference/<platform>/<endpoint> already
 * owns, which is parameters and response schema. Restating those here would
 * turn 204 slug-matched pairs into genuine duplicates of each other and
 * manufacture the cannibalization the noindex on that subtree exists to avoid.
 *
 * Everything is derived from the catalog and the measured window, so there is
 * nothing to keep up to date by hand, and a sentence whose input is missing is
 * dropped rather than softened.
 */
import type { BenchmarkSummary } from '../benchmarks';
import type { ApiDetail, Endpoint } from '../../features/catalog/model';
import { cheapest } from '../catalog';
import { m } from '../../paraglide/messages';
import { getLocale } from '../../paraglide/runtime';
import { API_BASE_URL } from '../../api/http';
import { fmtCount, usdCost } from '../format';
import { STARTER_BALANCE_USD, starterBalanceUsd } from '../signupOffer';
import { type AnswerQuestion, measuredSentence, priceClause, sentence, sentences } from './answerParts';

export interface EndpointAnswer {
  /** Self-contained lead paragraph, the passage an answer engine can lift. */
  passage: string;
  questions: AnswerQuestion[];
}

/** The platform and action every question names: "Amazon" and "Reviews". Kept
 *  apart so each language orders them itself; Portuguese puts the action first. */
const subjectOf = (api: ApiDetail, endpoint: Endpoint) => ({ name: api.name, action: endpoint.action });

/** English lowercases an action inside a sentence ("Amazon reviews calls"). A
 *  translated action title is a name, so it keeps the casing the page shows. */
const proseAction = (endpoint: Endpoint): string =>
  getLocale() === 'en' ? endpoint.action.toLowerCase() : endpoint.action;

/** The measured clause's subject: "Amazon reviews" calls. */
const measuredSubject = (api: ApiDetail, endpoint: Endpoint): string =>
  m.seo_measured_subject_endpoint({ name: api.name, action: proseAction(endpoint) });

/** What the opening says the call returns: "Amazon reviews data". A translated
 *  action title cannot qualify "data" ("dados de Buscar"), and the translated
 *  opening already names the endpoint, so a translation names the platform alone. */
const openingData = (api: ApiDetail, endpoint: Endpoint): string =>
  getLocale() === 'en' ? `${api.name} ${proseAction(endpoint)}` : api.name;

/** The endpoint's own blurb, the one genuinely unique sentence on the page. */
const description = (endpoint: Endpoint): string | undefined => {
  const text = endpoint.description.trim();
  return text ? sentence(text) : undefined;
};

function priceSentence(endpoint: Endpoint): string | undefined {
  if (endpoint.providers.length === 0) return undefined;
  const price = cheapest(endpoint).price;
  return m.seo_endpoint_price_sentence({ price: priceClause(price) });
}

/**
 * How the call routes.
 *
 * `failover` is the gateway-authoritative fact and is read directly; the
 * provider count is a display number and does not imply it. The single-provider
 * caveat is not spelled out: the passage says what does happen, and the
 * provider table below draws the same fact as a share column with no second
 * provider in it.
 */
function routingSentence(endpoint: Endpoint): string {
  const lanes = endpoint.providers.length;
  if (endpoint.failover && lanes > 1) {
    return m.seo_endpoint_routing_multi({ count: lanes });
  }
  return m.seo_endpoint_routing_single();
}

function passageFor(
  api: ApiDetail,
  endpoint: Endpoint,
  summary: BenchmarkSummary | null,
): string {
  const opening = m.seo_endpoint_opening({
    ...subjectOf(api, endpoint),
    measured: openingData(api, endpoint),
    method: endpoint.method,
    path: endpoint.path,
  });
  return sentences([
    opening,
    description(endpoint),
    routingSentence(endpoint),
    priceSentence(endpoint),
    measuredSentence(measuredSubject(api, endpoint), summary),
  ]);
}

function questionsFor(
  api: ApiDetail,
  endpoint: Endpoint,
  summary: BenchmarkSummary | null,
): AnswerQuestion[] {
  const subject = subjectOf(api, endpoint);
  const questions: AnswerQuestion[] = [];
  const price = priceSentence(endpoint);
  if (price) {
    questions.push({
      q: m.seo_endpoint_q_cost(subject),
      a: sentences([price, m.seo_wallet_answer()]),
    });
  }
  const blurb = description(endpoint);
  if (blurb) {
    questions.push({
      q: m.seo_endpoint_q_returns(subject),
      a: sentences([blurb, m.seo_endpoint_returns_note()]),
    });
  }
  const measured = measuredSentence(measuredSubject(api, endpoint), summary);
  if (measured) {
    questions.push({
      q: m.seo_endpoint_q_reliable(subject),
      a: sentences([measured, m.seo_measurement_note()]),
    });
  }
  const free = freeAnswer(endpoint, subject);
  if (free) questions.push({ q: m.seo_endpoint_q_free(subject), a: free });
  const failover = failoverAnswer(endpoint);
  if (failover) questions.push({ q: m.seo_endpoint_q_failover(subject), a: failover });
  // Only beside a question the catalog answered: on its own it would give a page
  // with no price and no blurb a passage it has nothing to say in.
  if (questions.length > 0) {
    questions.push(
      {
        q: m.seo_endpoint_q_call(subject),
        a: m.seo_endpoint_a_call({ method: endpoint.method, url: `${API_BASE_URL}${endpoint.path}` }),
      },
      { q: m.seo_endpoint_q_subscription(subject), a: m.seo_endpoint_a_subscription() },
      { q: m.seo_endpoint_q_sdk(subject), a: m.seo_endpoint_a_sdk() },
      {
        q: m.seo_endpoint_q_agent(subject),
        a: m.seo_endpoint_a_agent({ mcp: `${API_BASE_URL}/mcp`, signup: `${API_BASE_URL}/agent/signup` }),
      },
    );
  }
  return questions;
}

/**
 * The "is it free" question searchers ask of every API, answered from the signup
 * offer and this endpoint's own price: how many calls the starting balance buys
 * here, not on the cheapest endpoint the homepage quotes. A metered price is a
 * per-request ceiling, so its count is a floor. A price above the whole balance
 * drops the count rather than promising zero calls.
 */
function freeAnswer(endpoint: Endpoint, subject: { name: string; action: string }): string | undefined {
  if (endpoint.providers.length === 0) return undefined;
  const price = cheapest(endpoint).price;
  const calls = Math.floor(STARTER_BALANCE_USD / price.perRequestUsd);
  const balance = starterBalanceUsd();
  if (calls < 1) return m.seo_endpoint_a_free({ balance });
  const inputs = { ...subject, balance, calls: fmtCount(calls), each: usdCost(price.perRequestUsd) };
  return price.metered ? m.seo_endpoint_a_free_calls_metered(inputs) : m.seo_endpoint_a_free_calls(inputs);
}

/** Only for an endpoint the gateway actually fails over on, with a source to
 *  fall back to. It promises the fallback and nothing about the price of a
 *  failed call, which the wallet answer above states. */
function failoverAnswer(endpoint: Endpoint): string | undefined {
  const sources = endpoint.providers.length;
  if (!endpoint.failover || sources < 2) return undefined;
  return m.seo_endpoint_a_failover({ count: sources });
}

/**
 * Answer passage and Q&A for one endpoint page, or null when the endpoint has
 * no lane to price and no blurb to quote - the two inputs that make the page
 * worth a passage at all.
 */
export function endpointAnswer(
  api: ApiDetail,
  endpoint: Endpoint,
  summary: BenchmarkSummary | null,
): EndpointAnswer | null {
  const questions = questionsFor(api, endpoint, summary);
  if (questions.length === 0) return null;
  return { passage: passageFor(api, endpoint, summary), questions };
}
